import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useIntl } from "react-intl";
import moment, { Moment } from "moment";
import { useScenarioId, useModal, useWebsocket, ScenarioUploadOptionKey } from "@/hook";
import Button from "@/component/Button";
import { Modal, FileUploadZone } from "@/component/modal";
import { errorToast, successToast, warningToast } from "@/component/Toast";


export const SCENARIO_UPLOAD_MODAL_ID = "scenario-upload-modal-id";

const UploadScenarioModal: React.FC = () => {
    const intl = useIntl();
    const modal = useModal();
    const scenarioId = useScenarioId();
    const { requestScenarioUpload, addEventListener, removeEventListener } = useWebsocket();

    const checkboxRefs = useRef<{[key in ScenarioUploadOptionKey]: boolean}>({
        scenario: false,
        templates: false,
        constants: false,
        publisher: false,
        faq: false,
        properties: false,
    });
    const [file, setFile] = useState<File | null>(null);
    const importStartTime = useRef<Moment | null>(null);
    const importEndTime = useRef<Moment | null>(null);
    const [progressPercentage, setProgressPercentage] = useState<number>(0);
    const [testInfoText, setTestInfoText] = useState<string>("");
    const [importStatus, setImportStatus] = useState<"wait" | "progress" | "finish" | "error" | "warning">("wait");
    const isTestEnded = ["finish", "error", "warning"].includes(importStatus);

    const onClickConfirm = () => {
        if (isTestEnded) {
            modal.close(SCENARIO_UPLOAD_MODAL_ID);
            return;
        }
        if (importStatus === "progress") {
            return;
        }
        if (!file) {
            warningToast(intl.formatMessage({ id: "i02020" }));
            return;
        }
        try {
            importStartTime.current = moment();
            importEndTime.current = null;
            requestScenarioUpload(scenarioId, file, {
                ...checkboxRefs.current,
            });
            successToast(intl.formatMessage({ id: "i00007" }));
        } catch {
            importStartTime.current = null;
            errorToast(intl.formatMessage({ id: "i00008" }));
            return;
        }
    };

    const onClose = () => {
        setFile(null);
        setImportStatus("wait");
        setProgressPercentage(0);
        setTestInfoText("0%");
        setTestInfoText("");
        importStartTime.current = null;
        importEndTime.current = null;
        checkboxRefs.current = {
            scenario: false,
            templates: false,
            constants: false,
            publisher: false,
            faq: false,
            properties: false,
        };
    };

    useEffect(() => {
        const onReceiveStarted = () => {
            setImportStatus("progress");
            setProgressPercentage(0);
            setTestInfoText("0%");
        };
    
        const onReceiveProgress = (res: any) => {
            const { data } = res;
            if (data.index && data.total) {
                const percentage = Math.round((data.index / data.total) * 100);
                setProgressPercentage(percentage);
                setTestInfoText(`${percentage}% (${data.index}/${data.total}) - ${data.type}`);
            } else {
                setProgressPercentage(0);
                setTestInfoText(`0% - ${data.type}`);
            }
            setImportStatus("progress");
        };
    
        const onReceiveFinished = () => {
            importEndTime.current = moment();
            setImportStatus(prev => {
                if (prev !== "progress") {
                    return prev;
                }
                setProgressPercentage(100);
                setTestInfoText("100%");
                return "finish";
            });
        };
    
        const onReceiveError = (res: any) => {
            const { data } = res;
            if (data.error) {
                setImportStatus("error");
                setTestInfoText(`${intl.formatMessage({ id: "i02025" })} (${data.error})`);
            } else if (data.warning) {
                setImportStatus("warning");
                setTestInfoText(`${intl.formatMessage({ id: "i02026" })} (${data.warning})`);
            }
        };

        const listeners = [
            { event: "scenario_import_started", handler: onReceiveStarted },
            { event: "scenario_import_progress", handler: onReceiveProgress },
            { event: "scenario_import_finished", handler: onReceiveFinished },
            { event: "scenario_import_error", handler: onReceiveError },
        ] as const;

        listeners.forEach(({ event, handler }) => {
            addEventListener(event, handler);
        });

        return () => {
            listeners.forEach(({ event, handler }) => {
                removeEventListener(event, handler);
            });
        };
    }, [intl, importStatus, addEventListener, removeEventListener]);

    return (
        <Modal id={SCENARIO_UPLOAD_MODAL_ID}
               title={intl.formatMessage({ id: "b0004" })}
               onClose={onClose}
        >
            <div className="w-[780px] mt-6 flex flex-col gap-6">
                <FileUploadZone file={file}
                                onChangeFile={setFile}
                />
                {importStatus !== "wait" && (
                    <div className="-mt-2 flex flex-col items-center gap-1">
                        <progress value={progressPercentage}
                                  max={100}
                                  aria-label={testInfoText}
                                  className={classNames(
                                      "w-full h-4 rounded-full overflow-hidden appearance-none",
                                      "[&::-webkit-progress-bar]:bg-black-200 [&::-webkit-progress-bar]:rounded-full",
                                      "[&::-webkit-progress-value]:bg-chat42  [&::-webkit-progress-value]:rounded-full",
                                      "[&::-webkit-progress-value]:transition-all [&::-webkit-progress-value]:duration-300",
                                      "[&::-moz-progress-bar]:bg-chat42"
                                  )}
                        >
                            <span>
                                {testInfoText}
                            </span>
                            <span>
                                {!importStartTime.current
                                    ? "00:00"
                                    : moment
                                        .utc((importEndTime.current || moment()).diff(importStartTime.current))
                                        .format("mm:ss")
                                }
                            </span>
                        </progress>
                        <div aria-live="polite"
                             aria-atomic="true"
                             className="w-full flex justify-between items-center text-[12px] text-black-400"
                        >
                            <span className={classNames(
                                      "transition-all duration-300",
                                      importStatus === "error" && "text-point-red",
                                      importStatus === "warning" && "text-point-yellow",
                                  )}
                            >
                                {testInfoText}
                            </span>
                            <span>
                                {!importStartTime.current
                                    ? "00:00"
                                    : moment
                                        .utc((importEndTime.current || moment()).diff(importStartTime.current))
                                        .format("mm:ss")
                                }
                            </span>
                        </div>
                    </div>
                )}

                <fieldset className="flex gap-2">
                    <legend className="sr-only">
                        {intl.formatMessage({ id: "b0004" })}
                    </legend>
                    <label className="flex items-center gap-1 text-black-500 cursor-pointer">
                        <input type="checkbox"
                               className="input-checkbox-chat42"
                               onChange={e => { checkboxRefs.current.scenario = e.target.checked; }}
                        />
                        {intl.formatMessage({ id: "b0014" })}
                    </label>

                    <label className="flex items-center gap-1 text-black-500 cursor-pointer">
                        <input type="checkbox"
                               className="input-checkbox-chat42"
                               onChange={e => { checkboxRefs.current.templates = e.target.checked; }}
                        />
                        {intl.formatMessage({ id: "b0015" })}
                    </label>

                    <label className="flex items-center gap-1 text-black-500 cursor-pointer">
                        <input type="checkbox"
                               className="input-checkbox-chat42"
                               onChange={e => { checkboxRefs.current.constants = e.target.checked; }}
                        />
                        {intl.formatMessage({ id: "b0041" })}
                    </label>

                    <label className="flex items-center gap-1 text-black-500 cursor-pointer">
                        <input type="checkbox"
                               className="input-checkbox-chat42"
                               onChange={e => { checkboxRefs.current.publisher = e.target.checked; }}
                        />
                        {intl.formatMessage({ id: "i02022" })}
                    </label>

                    <label className="flex items-center gap-1 text-black-500 cursor-pointer">
                        <input type="checkbox"
                               className="input-checkbox-chat42"
                               onChange={e => { checkboxRefs.current.faq = e.target.checked; }}
                        />
                        {intl.formatMessage({ id: "b0040" })}
                    </label>

                    <label className="flex items-center gap-1 text-black-500 cursor-pointer">
                        <input type="checkbox"
                               className="input-checkbox-chat42"
                               onChange={e => { checkboxRefs.current.properties = e.target.checked; }}
                        />
                        {intl.formatMessage({ id: "i02023" })}
                    </label>
                </fieldset>

                <div className="modal-btn-group">
                    <Button size="sm"
                            variant="secondary"
                            disabled={importStatus === "progress"}
                            onClick={() => modal.close(SCENARIO_UPLOAD_MODAL_ID)}
                    >
                        {intl.formatMessage({ id: "b0002" })}
                    </Button>
                    <Button size="sm"
                            variant="primary"
                            disabled={importStatus === "progress" || (importStatus === "wait" && !file)}
                            onClick={onClickConfirm}
                    >
                        {isTestEnded ? intl.formatMessage({ id: "b0001" }) : intl.formatMessage({ id: "b0008" })}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default UploadScenarioModal;
